import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    QueryList,
    ViewChildren,
    ViewEncapsulation
} from "@angular/core";
import { Validators } from "@angular/forms";
import { GeneratedFormArray, GeneratedFormGroup, NgxFormGeneratorProvider } from "@recursyve/ngx-form-generator";
import { validators } from "tailwind-merge";
import { CodeCharacterForm, RegisterOwnerCodeForm } from "./register-owner-code.form";

@Component({
    selector: "nerri-register-owner-code-form",
    standalone: false,
    templateUrl: "./register-owner-code-form.template.html",
    styleUrls: ["./register-owner-code-form.style.scss"],
    encapsulation: ViewEncapsulation.None,
    providers: NgxFormGeneratorProvider.forFeature([RegisterOwnerCodeForm])
})
export class RegisterOwnerCodeFormComponent implements OnInit {
    @Input()
    public validating = false;

    @Input()
    public hasValidationError = false;

    @Output()
    public readonly code = new EventEmitter<string>();

    @ViewChildren("characters")
    protected characters: QueryList<ElementRef>;

    protected formArray: GeneratedFormArray<CodeCharacterForm>;

    private static readonly ACCOUNT_CREATION_CODE_LENGTH = 6;

    constructor(public formGroup: GeneratedFormGroup<RegisterOwnerCodeForm>) {}

    public ngOnInit(): void {
        this.formArray = this.formGroup.get("characters") as GeneratedFormArray<CodeCharacterForm>;

        for (let i = 0; i < RegisterOwnerCodeFormComponent.ACCOUNT_CREATION_CODE_LENGTH; i++) {
            this.formArray.push({});
        }
    }

    public clickSubmit(): void {
        if (this.formGroup.invalid) {
            this.formGroup.markAllAsTouched();
            return;
        }

        const codes = this.formGroup.getRawValue();
        const characters = codes.characters.map(character => character.value?.trim()).filter(character => !!character);
        if (characters.length !== RegisterOwnerCodeFormComponent.ACCOUNT_CREATION_CODE_LENGTH) {
            this.addRequiredValidator();
            return;
        }

        this.code.emit(characters.join(""));
    }

    protected onFocus(event: FocusEvent, index: number): void {
        if (event.target !== event.currentTarget) {
            return;
        }

        const previousCharacter = this.characters.get(index - 1);
        if (previousCharacter && previousCharacter.nativeElement.value === "") {
            previousCharacter.nativeElement.focus();
        }
    }

    protected keyup(event: KeyboardEvent, index: number): void {
        if (event.key === "Backspace") {
            this.characters.get(index - 1)?.nativeElement.focus();
            return;
        }

        const nextCharacter = this.characters.get(index + 1);
        if (nextCharacter) {
            nextCharacter.nativeElement.focus();
        } else {
            this.characters.get(index)?.nativeElement.blur();
            this.clickSubmit();
        }
    }

    protected onPaste(event: ClipboardEvent): void {
        const data = event.clipboardData?.getData("Text");
        if (!data) {
            return;
        }

        if (data.length !== RegisterOwnerCodeFormComponent.ACCOUNT_CREATION_CODE_LENGTH) {
            return;
        }

        this.formArray.patchValue(data.split("").map(character => ({ value: character })));
        this.clickSubmit();
    }

    protected addRequiredValidator(): void {
        this.formArray.controls.forEach(control => {
            control.get("value")?.addValidators([Validators.required]);
            control.get("value")?.updateValueAndValidity();
        });
    }
}
